import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { useParams } from 'react-router-dom'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import Skeleton from '@material-ui/lab/Skeleton'
import ArrowBackIcon from 'components/material-symbol/icons/ArrowBack'
import EventBusyIcon from 'components/material-symbol/icons/EventBusy'
import { useAuth } from 'lib/auth'
import LoadError from './load-error'
import {
  usePreparePurchaseVirtualCoaching,
  useVirtualCoachingOffering,
} from './api-hooks'
import { trackVirtualCoachingCheckoutViewed } from './analytics'
import OrderSummary from './order-summary'
import PaymentPanel from './payment-panel'
import { useStyles } from './styles'
import { getCoachProfileUrl, isOfferingAvailable } from './utils'

const CheckoutSkeleton = () => {
  const classes = useStyles()
  return (
    <Grid
      container
      spacing={3}
      className={classes.checkoutGrid}
      data-testid="virtual-coaching-checkout-skeleton"
      aria-busy="true"
    >
      {[0, 1].map(index => (
        <Grid item xs={12} md={6} key={index}>
          <Skeleton variant="rect" height={index === 0 ? 480 : 360} />
        </Grid>
      ))}
    </Grid>
  )
}

export const OfferingUnavailable = ({ idOrSlug }) => {
  const classes = useStyles()
  const profileUrl = getCoachProfileUrl(idOrSlug)
  return (
    <section className={classes.unavailableCard}>
      <Box className={classes.confirmIcon} aria-hidden="true">
        <EventBusyIcon />
      </Box>
      <Typography
        variant="h2"
        className={classes.unavailableTitle}
        data-testid="offering-unavailable-title"
      >
        This Virtual Coaching offering is no longer available
      </Typography>
      <Typography className={classes.muted}>
        It may have ended, reached its purchase limit, or been removed by your
        coach. Check your coach&apos;s profile for what they offer now.
      </Typography>
      <Button
        variant="contained"
        color="primary"
        component="a"
        href={profileUrl}
        className={classes.unavailableAction}
      >
        View coach profile
      </Button>
    </section>
  )
}

OfferingUnavailable.propTypes = { idOrSlug: PropTypes.string.isRequired }

// Everything after the offering has loaded: the two columns. A separate
// component so the prepare-purchase mutation only runs for a purchasable
// offering and a signed-in golfer.
export const CheckoutContent = ({ offering, idOrSlug, lessonTypeId, user }) => {
  const prepare = usePreparePurchaseVirtualCoaching(lessonTypeId, {
    enabled: Boolean(user),
  })

  useEffect(() => {
    trackVirtualCoachingCheckoutViewed()
  }, [])

  return (
    <CheckoutLayout
      offering={offering}
      idOrSlug={idOrSlug}
      lessonTypeId={lessonTypeId}
      user={user}
      prepare={prepare}
    />
  )
}

// Presentational: the dev preview renders this directly with fixtures.
export const CheckoutLayout = ({
  offering,
  idOrSlug,
  lessonTypeId,
  user,
  prepare,
  PaymentForm,
}) => {
  const classes = useStyles()
  return (
    <Grid container spacing={3} className={classes.checkoutGrid}>
      <Grid item xs={12} md={6}>
        <OrderSummary offering={offering} />
      </Grid>
      <Grid item xs={12} md={6}>
        <PaymentPanel
          offering={offering}
          idOrSlug={idOrSlug}
          lessonTypeId={lessonTypeId}
          user={user}
          prepare={prepare}
          PaymentForm={PaymentForm}
        />
      </Grid>
    </Grid>
  )
}

CheckoutLayout.propTypes = {
  offering: PropTypes.object.isRequired,
  idOrSlug: PropTypes.string.isRequired,
  lessonTypeId: PropTypes.string.isRequired,
  user: PropTypes.object,
  prepare: PropTypes.object.isRequired,
  PaymentForm: PropTypes.elementType,
}

CheckoutContent.propTypes = {
  offering: PropTypes.object.isRequired,
  idOrSlug: PropTypes.string.isRequired,
  lessonTypeId: PropTypes.string.isRequired,
  user: PropTypes.object,
}

export const CheckoutFrame = ({ idOrSlug, children }) => {
  const classes = useStyles()
  return (
    <Box className={classes.page}>
      <Button
        className={classes.backLink}
        href={getCoachProfileUrl(idOrSlug)}
        startIcon={<ArrowBackIcon />}
      >
        Back to coach profile
      </Button>
      <Typography variant="h1" className={classes.srOnly}>
        Checkout
      </Typography>
      {children}
    </Box>
  )
}

CheckoutFrame.propTypes = {
  idOrSlug: PropTypes.string.isRequired,
  children: PropTypes.node,
}

const VirtualCoachingCheckout = () => {
  const { idOrSlug, lessonTypeId } = useParams()
  const { isLoggedIn, user } = useAuth()
  const { data, loading, error, refetch } = useVirtualCoachingOffering(
    lessonTypeId,
  )
  const offering = data?.virtualCoachingOffering

  let content
  if (loading && !data) {
    content = <CheckoutSkeleton />
  } else if (!offering && error?.networkError) {
    content = (
      <LoadError
        error={error}
        title="We couldn't load this Virtual Coaching offering"
        onRetry={() => refetch()}
      />
    )
  } else if (!isOfferingAvailable(offering)) {
    // The backend returns null for an offering golfers cannot buy (ended,
    // purchase limit reached, removed).
    content = <OfferingUnavailable idOrSlug={idOrSlug} />
  } else {
    content = (
      <CheckoutContent
        offering={offering}
        idOrSlug={idOrSlug}
        lessonTypeId={lessonTypeId}
        user={isLoggedIn ? user : null}
      />
    )
  }

  return <CheckoutFrame idOrSlug={idOrSlug}>{content}</CheckoutFrame>
}

export default VirtualCoachingCheckout
